import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { FiTerminal, FiAlertTriangle, FiX, FiSend, FiArrowRight, FiSearch, FiCheckSquare, FiSquare } from 'react-icons/fi';
import { getAllLicenses } from '../../services/licenseService';
import { getInstances } from '../../services/monitorService';
import { createCommand, getCommands, cancelCommand } from '../../services/commandService';
import {
  commandTypeName, commandStatusMeta, fmtTime
} from '../../utils/format';

// 指令类型配置(图标/颜色/危险等级)
const COMMAND_META = {
  CLOSE_ALL: { color: '#cf222e', bg: '#ffebe9', danger: true, desc: '平掉目标下的所有持仓(含全部实例)' },
  PAUSE: { color: '#9a6700', bg: '#fff8c5', danger: false, desc: '停止开新单, 保留现有持仓' },
  RESUME: { color: '#1a7f37', bg: '#dafbe1', danger: false, desc: '恢复 EA 正常交易' },
  CLOSE: { color: '#9a6700', bg: '#fff8c5', danger: false, desc: '按 signalId 平掉对应跟单仓' }
};
// 需要payload表单的指令类型(云端开多/开空已下线: 开仓信号只走推送器链路)
const PAYLOAD_TYPES = ['CLOSE'];

const CommandCenter = () => {
  const queryClient = useQueryClient();

  // 授权码列表(本地搜索: 客户名称/账户/授权码/应用名)
  const { data: licensesData } = useQuery('commandLicenses', () =>
    getAllLicenses(1, 200, '', '', '', '')
  );
  const licenses = licensesData?.licenses || [];

  // 实例列表(目标实例选择)
  const { data: instancesData } = useQuery('instances', getInstances, { refetchInterval: 30000 });
  const allInstances = instancesData?.instances || [];

  // 排队中的指令
  const { data: pendingData } = useQuery(['commands', 'pending'], () =>
    getCommands({ status: 'pending', limit: 20 })
  );
  // 已拉取未执行的指令
  const { data: pickedData } = useQuery(['commands', 'picked'], () =>
    getCommands({ status: 'picked', limit: 20 })
  );

  const [keyword, setKeyword] = React.useState('');
  // 多选: selectedLicenseIds 为已勾选的授权码ID集合; 点列表行=单选(只留这一个)
  const [selectedLicenseIds, setSelectedLicenseIds] = React.useState([]);
  const [selectedInstance, setSelectedInstance] = React.useState(''); // '' = 全部实例
  const [confirming, setConfirming] = React.useState(null); // 待确认的指令类型
  const [confirmText, setConfirmText] = React.useState('');
  const [closeSignalId, setCloseSignalId] = React.useState('');

  // 关键词过滤: 客户名称 / 账户 / 授权码 / 应用名 任一命中即展示
  const kw = keyword.trim().toLowerCase();
  const filteredLicenses = kw
    ? licenses.filter(l =>
        (l.customerName || '').toLowerCase().includes(kw) ||
        (l.account || '').toLowerCase().includes(kw) ||
        (l.licenseKey || '').toLowerCase().includes(kw) ||
        (l.application?.name || '').toLowerCase().includes(kw)
      )
    : licenses;

  const selectedLicenses = licenses.filter(l => selectedLicenseIds.includes(l.id));
  // 已选范围的实例并集(单授权码=它自己的实例; 多选=跨授权码聚合, 支持按应用/客户批量下发)
  const scopeInstances = selectedLicenses.length > 0
    ? allInstances.filter(i => selectedLicenseIds.includes(i.licenseId))
    : [];
  // 目标实例按授权码分组(组内即该授权码的实例, 按账户展示)
  const instanceGroups = React.useMemo(() => {
    const map = new Map();
    for (const i of scopeInstances) {
      const key = i.licenseId;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(i);
    }
    return [...map.entries()].map(([licenseId, list]) => {
      const lic = licenses.find(l => l.id === licenseId);
      return {
        licenseId,
        label: `${lic?.customerName || '未命名客户'} · ${(lic?.licenseKey || '').slice(0, 14)}...`,
        list
      };
    });
  }, [scopeInstances, licenses]);
  const selectedInstanceInfo = scopeInstances.find(i => i.instanceId === selectedInstance) || null;
  // 危险确认凭据: 指定实例=该账户后4位; 全部实例=输入固定确认文字
  const confirmCredential = selectedInstanceInfo
    ? (selectedInstanceInfo.accountLogin || selectedInstanceInfo.instanceId).slice(-4)
    : null;

  const onlineCount = scopeInstances.filter(i => i.isOnline).length;

  // 勾选切换(多选)
  const toggleLicense = (id) => {
    setSelectedInstance('');
    setSelectedLicenseIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };
  // 点行=单选(只保留这一个; 再点已选中的取消)
  const selectLicenseOnly = (id) => {
    setSelectedInstance('');
    setSelectedLicenseIds(prev => (prev.length === 1 && prev[0] === id ? [] : [id]));
  };
  // 全选/清空当前筛选结果
  const toggleSelectAllFiltered = () => {
    const filteredIds = filteredLicenses.map(l => l.id);
    const allSelected = filteredIds.length > 0 && filteredIds.every(id => selectedLicenseIds.includes(id));
    setSelectedInstance('');
    setSelectedLicenseIds(allSelected ? [] : filteredIds);
  };
  const allFilteredSelected = filteredLicenses.length > 0 &&
    filteredLicenses.every(l => selectedLicenseIds.includes(l.id));

  // 下发指令
  const createMutation = useMutation(createCommand, {
    onSuccess: (data) => {
      if (data.skipped && data.skipped.length > 0) {
        const reasons = {};
        data.skipped.forEach(s => { reasons[s.reason] = (reasons[s.reason] || 0) + 1; });
        const summary = Object.entries(reasons).map(([r, n]) => `${r}×${n}`).join(', ');
        toast.success(`${data.message}（跳过 ${data.skipped.length} 个: ${summary}）`);
      } else {
        toast.success(data.message || '指令已下发');
      }
      queryClient.invalidateQueries(['commands']);
      queryClient.invalidateQueries('platformOverview');
      closeConfirm();
    },
    onError: (err) => {
      toast.error(err.message || '下发失败');
    }
  });

  // 取消指令
  const cancelMutation = useMutation(cancelCommand, {
    onSuccess: () => {
      toast.success('指令已取消');
      queryClient.invalidateQueries(['commands']);
      queryClient.invalidateQueries('platformOverview');
    },
    onError: (err) => toast.error(err.message || '取消失败')
  });

  const closeConfirm = () => {
    setConfirming(null);
    setConfirmText('');
  };

  const openConfirm = (type) => {
    if (selectedLicenseIds.length === 0) {
      toast.error('请先选择授权码（可多选批量下发）');
      return;
    }
    setConfirmText('');
    setConfirming(type);
  };

  const submitCommand = () => {
    if (selectedLicenseIds.length === 0 || !confirming) return;
    // 危险指令二次确认:
    //  - 指定实例/账户: 输入该账户后4位
    //  - 全部实例: 输入"确认下发指令"
    if (COMMAND_META[confirming].danger) {
      if (selectedInstanceInfo) {
        if (confirmText.trim() !== confirmCredential) {
          toast.error(`请输入账户后4位 ${confirmCredential} 以确认`);
          return;
        }
      } else {
        if (confirmText.trim() !== '确认下发指令') {
          toast.error('全部实例危险操作, 请输入"确认下发指令"以确认');
          return;
        }
      }
    }
    // 跟单平仓指令: 组装payload(开仓类指令已下线, 只保留CLOSE)
    let payload = null;
    if (confirming === 'CLOSE') {
      if (!closeSignalId.trim()) { toast.error('请填写要平仓的 signalId'); return; }
      payload = { signalId: closeSignalId.trim() };
    }
    createMutation.mutate({
      licenseIds: selectedLicenseIds,
      instanceId: selectedInstance || null,
      commandType: confirming,
      payload
    });
  };

  const pendingList = [...(pendingData?.commands || []), ...(pickedData?.commands || [])];

  return (
    <div className="space-y-6">
      {/* 页头 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#1f2328]">指令中心</h1>
          <p className="text-[#656d76] text-sm mt-1">远程控制 EA：全部平仓、暂停开新单、恢复运行（指令经 EA 轮询拉取后执行）</p>
        </div>
        <Link
          to="/commands/history"
          className="flex items-center gap-1 text-sm text-[#0969da] border border-[#d0d7de] rounded-md px-3 py-1.5 bg-white hover:bg-[#f6f8fa]"
        >
          执行记录 <FiArrowRight />
        </Link>
      </div>

      {/* 安全提示 */}
      <div className="flex items-start gap-2 bg-[#fff8c5] border border-[#f0d875] rounded-md p-3 text-xs text-[#7d4e00]">
        <FiAlertTriangle className="shrink-0 mt-0.5" />
        <span>平仓指令直接作用于真实账户资金，执行不可撤销。请确认目标实例无误后再下发。</span>
      </div>

      {/* 指令下发表单 */}
      <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm p-5 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 授权码选择: 搜索 + 多选列表(点行单选, 勾选框多选) */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm text-[#1f2328]">
                选择目标 <span className="text-[#8b949e] text-xs">(客户名称 / 账户 / 授权码 / 应用, 可多选批量下发)</span>
              </label>
              <button
                type="button"
                onClick={toggleSelectAllFiltered}
                disabled={filteredLicenses.length === 0}
                className="flex items-center gap-1 text-xs text-[#0969da] hover:underline disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
              >
                {allFilteredSelected ? <FiCheckSquare /> : <FiSquare />}
                {allFilteredSelected ? '清空选择' : `全选筛选结果 (${filteredLicenses.length})`}
              </button>
            </div>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8b949e]" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索客户名称 / 账户 / 授权码 / 应用"
                className="w-full border border-[#d0d7de] rounded-md pl-9 pr-3 py-2 text-sm bg-white focus:outline-none focus:border-[#0969da]"
              />
            </div>
            <div className="mt-2 border border-[#d0d7de] rounded-md max-h-52 overflow-y-auto divide-y divide-[#eaeef2]">
              {filteredLicenses.length === 0 ? (
                <div className="px-3 py-6 text-center text-[#8b949e] text-xs">没有匹配的授权码</div>
              ) : (
                filteredLicenses.map(l => {
                  const checked = selectedLicenseIds.includes(l.id);
                  return (
                    <div
                      key={l.id}
                      className={`w-full text-left px-3 py-2 hover:bg-[#f6f8fa] transition-colors flex items-start gap-2 ${checked ? 'bg-[#ddf4ff]' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleLicense(l.id)}
                        className="mt-1 w-4 h-4 rounded accent-blue-500 shrink-0 cursor-pointer"
                      />
                      <button
                        type="button"
                        onClick={() => selectLicenseOnly(l.id)}
                        className="flex-1 min-w-0 text-left"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-[#1f2328] truncate font-medium">
                            {l.customerName || '未命名客户'}
                          </span>
                          {l.status !== 'active' && (
                            <span className="text-[10px] px-1 rounded bg-[#f6f8fa] text-[#57606a] shrink-0">{l.status}</span>
                          )}
                          {l.isOnline && <span className="w-1.5 h-1.5 rounded-full bg-[#1a7f37] shrink-0" title="在线" />}
                        </div>
                        <div className="text-[#656d76] text-xs font-mono truncate">
                          {l.licenseKey}{l.account ? ` · 账户 ${l.account}` : ''}
                        </div>
                        <div className="text-[#8b949e] text-xs truncate">
                          {l.application?.name || '未知应用'}
                        </div>
                      </button>
                    </div>
                  );
                })
              )}
            </div>
            {selectedLicenseIds.length > 0 && (
              <p className="text-[#0969da] text-xs mt-1.5">
                已选 {selectedLicenseIds.length} 个授权码 · {onlineCount} 个在线实例
              </p>
            )}
          </div>

          {/* 目标实例选择: 已选范围的实例, 按授权码分组 */}
          <div>
            <label className="block text-sm text-[#1f2328] mb-1.5">目标实例</label>
            <select
              value={selectedInstance}
              onChange={(e) => setSelectedInstance(e.target.value)}
              disabled={selectedLicenseIds.length === 0}
              className="w-full border border-[#d0d7de] rounded-md px-3 py-2 text-sm bg-white disabled:bg-[#f6f8fa] disabled:text-[#8b949e] focus:outline-none focus:border-[#0969da]"
            >
              <option value="">全部实例（{onlineCount} 个在线实例）</option>
              {instanceGroups.map(group => (
                <optgroup key={group.licenseId} label={`${group.label}（${group.list.filter(i => i.isOnline).length} 在线）`}>
                  {group.list.map(i => (
                    <option key={i.instanceId} value={i.instanceId}>
                      {i.accountLogin || i.instanceId.slice(0, 12)} · {i.instanceId.slice(0, 12)}... · {i.isOnline ? '在线' : '离线'}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {selectedLicenseIds.length > 0 && (
              <p className="text-[#8b949e] text-xs mt-2">
                指令将下发至已选的 {selectedLicenseIds.length} 个授权码
                {selectedInstance ? '的指定实例' : '下的全部实例'}
              </p>
            )}
            {selectedLicenseIds.length > 1 && (
              <p className="text-[#9a6700] text-xs mt-1">
                批量下发：每个授权码各生成一条指令；已有同类型排队指令的授权码会自动跳过
              </p>
            )}
          </div>
        </div>

        {/* 指令按钮 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Object.keys(COMMAND_META).map(type => {
            const meta = COMMAND_META[type];
            return (
              <button
                key={type}
                onClick={() => openConfirm(type)}
                disabled={selectedLicenseIds.length === 0 || createMutation.isLoading}
                className="text-left p-4 rounded-md border border-[#d0d7de] bg-white hover:border-[#8b949e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="p-1.5 rounded-md" style={{ backgroundColor: meta.bg, color: meta.color }}>
                    <FiTerminal />
                  </span>
                  <span className="font-medium text-[#1f2328] text-sm">{commandTypeName(type)}</span>
                  {meta.danger && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#ffebe9] text-[#cf222e]">危险</span>
                  )}
                </div>
                <p className="text-[#656d76] text-xs">{meta.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* 排队中的指令 */}
      <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm">
        <div className="px-5 py-3 border-b border-[#d0d7de]">
          <h2 className="text-[#1f2328] font-medium">排队中的指令</h2>
        </div>
        {pendingList.length === 0 ? (
          <div className="px-5 py-10 text-center text-[#656d76] text-sm">当前没有排队中的指令</div>
        ) : (
          <div className="divide-y divide-[#eaeef2]">
            {pendingList.map(cmd => {
              const meta = commandStatusMeta(cmd.status);
              return (
                <div key={cmd.id} className="px-5 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-[#1f2328]">{commandTypeName(cmd.commandType)}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${meta.cls}`}>{meta.name}</span>
                    </div>
                    <div className="text-[#656d76] text-xs mt-0.5 font-mono truncate">
                      {cmd.licenseKey} · 目标: {cmd.instanceId ? (cmd.instanceId.slice(0, 16) + '...') : '全部实例'} · 下发于 {fmtTime(cmd.createdAt)}
                    </div>
                  </div>
                  <button
                    onClick={() => cancelMutation.mutate(cmd.id)}
                    disabled={cmd.status !== 'pending' || cancelMutation.isLoading}
                    className="shrink-0 flex items-center gap-1 text-xs text-[#cf222e] border border-[#ffebe9] bg-white rounded-md px-2.5 py-1.5 hover:bg-[#fff5f5] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <FiX /> 取消
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 确认弹窗 */}
      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-semibold text-[#1f2328]">
              确认下发「{commandTypeName(confirming)}」指令
            </h3>
            <div className="text-sm text-[#57606a] space-y-1">
              <p>
                授权码: <span className="text-[#1f2328]">{selectedLicenses.length} 个</span>
                {selectedLicenses.length <= 3 && (
                  <span className="font-mono text-xs text-[#656d76]">（{selectedLicenses.map(l => l.licenseKey.slice(0, 10)).join(', ')}...）</span>
                )}
              </p>
              <p>
                目标: {selectedInstanceInfo
                  ? <>账户 <span className="font-mono text-[#1f2328]">{selectedInstanceInfo.accountLogin || '未识别'}</span> · 实例 {selectedInstance.slice(0, 16)}...</>
                  : <>全部实例（{onlineCount} 个在线）</>}
              </p>
              <p className="text-[#8b949e] text-xs mt-2">
                指令将在 EA 下次轮询时拉取执行(最快约1秒); 若终端离线, 指令排队 24 小时后过期。
              </p>
            </div>

            {/* 跟单平仓指令: payload表单 */}
            {PAYLOAD_TYPES.includes(confirming) && (
              <div className="border border-[#d0d7de] rounded-md p-3 space-y-3 bg-[#f6f8fa]">
                {confirming === 'CLOSE' && (
                  <div>
                    <label className="block text-xs text-[#57606a] mb-1">要平仓的 signalId</label>
                    <input
                      value={closeSignalId}
                      onChange={(e) => setCloseSignalId(e.target.value)}
                      placeholder="开仓信号的 signalId"
                      className="w-full border border-[#d0d7de] rounded-md px-2.5 py-1.5 text-sm font-mono bg-white focus:outline-none focus:border-[#0969da]"
                    />
                    <p className="text-[10px] text-[#8b949e] mt-1">按开仓信号平掉客户侧对应的跟单仓(客户 EA 本地按 signalId 映射成交单)</p>
                  </div>
                )}
              </div>
            )}

            {COMMAND_META[confirming].danger && (
              <div>
                {selectedInstanceInfo ? (
                  <label className="block text-sm text-[#1f2328] mb-1.5">
                    危险操作确认: 请输入账户后 4 位
                    <span className="font-mono text-[#cf222e] ml-1">{confirmCredential}</span>
                  </label>
                ) : (
                  <label className="block text-sm text-[#1f2328] mb-1.5">
                    危险操作确认: 批量平仓 {selectedLicenses.length} 个授权码的全部实例, 请输入
                    <span className="font-mono text-[#cf222e] ml-1">确认下发指令</span>
                  </label>
                )}
                <input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  maxLength={selectedInstanceInfo ? 4 : 10}
                  placeholder={selectedInstanceInfo ? '账户后4位' : '确认下发指令'}
                  className="w-full border border-[#d0d7de] rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#cf222e]"
                />
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={closeConfirm}
                className="px-4 py-2 text-sm border border-[#d0d7de] rounded-md bg-white hover:bg-[#f6f8fa]"
              >
                取消
              </button>
              <button
                onClick={submitCommand}
                disabled={createMutation.isLoading}
                className="flex items-center gap-1 px-4 py-2 text-sm rounded-md text-white disabled:opacity-50"
                style={{ backgroundColor: COMMAND_META[confirming].color }}
              >
                <FiSend /> {createMutation.isLoading ? '下发中...' : '确认下发'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommandCenter;
